import { parse } from '@foxglove/rosmsg';
import { parseRos2idl } from '@foxglove/ros2idl-parser';
import {
  MessageReader as Ros2MessageReader,
  MessageWriter as Ros2MessageWriter,
} from '@foxglove/rosmsg2-serialization';
import {
  type Channel,
  type ConnectionGraphUpdate,
  FoxgloveClient,
  type IWebSocket,
  type MessageData,
  type Parameter,
  type ParameterValue,
  type ParameterValues,
  type Service,
  type ServiceCallFailure,
  type ServiceCallResponse,
} from '@foxglove/ws-protocol';
import EventEmitter from 'eventemitter3';

// The SDK-based foxglove_bridge negotiates `foxglove.sdk.v1`; the legacy constant is
// offered too for older bridges. Native browser WebSocket satisfies IWebSocket.
const SUBPROTOCOLS = ['foxglove.sdk.v1', FoxgloveClient.SUPPORTED_SUBPROTOCOL];

const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 10_000;
// Liveness: how often the watchdog checks that drips are still arriving.
const LIVENESS_CHECK_MS = 2_000;
// If no inbound traffic arrives within this window we treat the socket as dead and
// force a reconnect. A browser WebSocket does NOT fire 'close' promptly when the
// underlying TCP dies silently (a network blip) — it can hang for minutes until the
// OS TCP timeout. This watchdog is what actually detects that case. Liveness is fed
// by the `/drip` topic (an empty message published ~1Hz by the `drip` service) plus
// any other inbound traffic, so an otherwise-idle link still stays proven-alive.
const LIVENESS_TIMEOUT_MS = 15_000;
// Dedicated liveness topic: the `drip` package drips empty std_msgs/Empty here.
const DRIP_TOPIC = '/drip';
// How long to wait for a topic/service to appear in the graph before giving up.
const DISCOVERY_TIMEOUT_MS = 15_000;
// Default per-service-call timeout. Long-running calls (action get_result) pass 0.
const SERVICE_TIMEOUT_MS = 15_000;

export interface EventTypes {
  connection: () => void;
  close: (event?: CloseEvent) => void;
  error: (error: Error) => void;
}

export interface Publisher<T> {
  publish: (message: T) => void;
  unadvertise: () => void;
}

export interface Subscription {
  unsubscribe: () => void;
}

/** Rejection used for in-flight requests when the socket drops. */
export class DisconnectedError extends Error {
  constructor(msg = 'WebSocket disconnected before the request completed') {
    super(msg);
    this.name = 'DisconnectedError';
  }
}

export type ServiceCallOptions = { timeoutMs?: number };

type Subscriber = (message: unknown) => void;

interface SubEntry {
  subId?: number;
  reader?: Ros2MessageReader;
  callbacks: Set<Subscriber>;
}

interface PubEntry {
  pubId?: number;
  messageType?: string; // if omitted, derived from the advertised channel's schemaName
  writer?: Ros2MessageWriter;
  refCount: number;
}

interface PendingCall {
  reject: (err: Error) => void;
}

export class Impl {
  readonly emitter = new EventEmitter<EventTypes>();

  readonly _url: string;
  _client!: FoxgloveClient;
  _ws?: WebSocket;
  _connected = false;
  _closed = false;
  _reconnectAttempts = 0;
  _reconnectTimer?: ReturnType<typeof setTimeout>;

  // Liveness watchdog state.
  _lastActivityAt = 0;
  _heartbeatTimer?: ReturnType<typeof setInterval>;

  _connecting!: Promise<void>;
  _resolveConnecting!: () => void;

  // Reader/writer caches keyed by message schema name (schema is stable per name).
  _messageReaders = new Map<string, Ros2MessageReader>();
  _messageWriters = new Map<string, Ros2MessageWriter>();

  // Graph state, rebuilt from advertise/advertiseServices on every (re)connect.
  _channelsById = new Map<number, Channel>();
  _channelsByName = new Map<string, Channel>();
  _servicesById = new Map<number, Service>();
  _servicesByName = new Map<string, Service>();

  // Reconnect-aware bookkeeping: survives disconnects so we can re-establish.
  _subscriptions = new Map<string, SubEntry>(); // topic -> entry
  _subTopicById = new Map<number, string>(); // subId -> topic
  _publishers = new Map<string, PubEntry>(); // topic -> entry
  _pendingCalls = new Map<number, PendingCall>();

  _callId = 0;
  _paramId = 0;

  constructor(url: string) {
    this._url = url;
    this._resetConnecting();
    this._connect();
    // Subscribe to the liveness tap. The empty callback is enough: the generic
    // 'message' handler marks activity for every inbound message, and this
    // subscription survives reconnects via _reestablish. If `/drip` isn't advertised
    // (drip service off) the sub stays pending and liveness falls back to whatever
    // other traffic the socket carries.
    void this.createSubscription(DRIP_TOPIC, () => { });
  }

  _resetConnecting() {
    this._connecting = new Promise<void>((resolve) => {
      this._resolveConnecting = resolve;
    });
  }

  _connect() {
    this._connected = false;
    this._stopHeartbeat();
    this._channelsById.clear();
    this._channelsByName.clear();
    this._servicesById.clear();
    this._servicesByName.clear();
    this._subTopicById.clear();
    // Old subscription/publisher ids are invalid on a fresh socket.
    this._subscriptions.forEach((entry) => { entry.subId = undefined; });
    this._publishers.forEach((entry) => { entry.pubId = undefined; });

    const ws = new WebSocket(this._url, SUBPROTOCOLS);
    this._ws = ws;
    const client = new FoxgloveClient({ ws: ws as unknown as IWebSocket });
    this._client = client;

    // Events from a superseded client (e.g. the delayed 'close' of a socket we already
    // force-dropped) must be ignored so they don't clobber the live connection.
    const isCurrent = () => this._client === client;
    // Any inbound traffic proves the link is alive; feed the liveness watchdog.
    const markActivity = () => {
      if (isCurrent()) this._lastActivityAt = Date.now();
    };

    let opened = false;
    let gotServerInfo = false;
    const maybeConnected = () => {
      if (!opened || !gotServerInfo || this._connected) return;
      this._connected = true;
      this._reconnectAttempts = 0;
      this._startHeartbeat();
      // Re-subscribe / re-advertise whatever's already advertised; anything not yet
      // advertised is established later by the 'advertise' handler.
      this._reestablish();
      this._resolveConnecting();
      this.emitter.emit('connection');
    };

    client.on('open', () => {
      if (!isCurrent()) return;
      opened = true;
      markActivity();
      maybeConnected();
    });
    client.on('serverInfo', () => {
      if (!isCurrent()) return;
      gotServerInfo = true;
      markActivity();
      maybeConnected();
    });
    client.on('error', (error?: Error) => {
      if (!isCurrent()) return;
      this.emitter.emit('error', error ?? new Error('WebSocket error'));
    });
    client.on('close', (event?: CloseEvent) => {
      if (!isCurrent()) return;
      this._onClose(event);
    });

    // Liveness taps: mark activity on any server-originated message.
    client.on('message', markActivity);
    client.on('time', markActivity);
    client.on('status', markActivity);
    client.on('parameterValues', markActivity);
    client.on('advertiseServices', markActivity);

    this._client.on('advertise', (channels) => {
      markActivity();
      for (const channel of channels) {
        this._channelsById.set(channel.id, channel);
        this._channelsByName.set(channel.topic, channel);
        // A topic we want may have just appeared — establish any pending sub/pub.
        if (this._connected) {
          this._establishSubscription(channel.topic);
          this._establishPublisher(channel.topic);
        }
      }
    });
    this._client.on('unadvertise', (channelIds) => {
      for (const id of channelIds) {
        const ch = this._channelsById.get(id);
        if (ch) {
          this._channelsById.delete(ch.id);
          this._channelsByName.delete(ch.topic);
        }
      }
    });
    this._client.on('advertiseServices', (services) => {
      for (const s of services) {
        this._servicesById.set(s.id, s);
        this._servicesByName.set(s.name, s);
      }
    });
    this._client.on('unadvertiseServices', (serviceIds) => {
      for (const id of serviceIds) {
        const s = this._servicesById.get(id);
        if (s) {
          this._servicesById.delete(s.id);
          this._servicesByName.delete(s.name);
        }
      }
    });

    // Single message dispatcher: subId -> topic -> fan out to callbacks.
    this._client.on('message', (event: MessageData) => {
      const topic = this._subTopicById.get(event.subscriptionId);
      if (topic === undefined) return;
      const entry = this._subscriptions.get(topic);
      if (!entry?.reader) return;
      const msg = entry.reader.readMessage(event.data);
      entry.callbacks.forEach((cb) => cb(msg));
    });
  }

  _onClose(event?: CloseEvent) {
    this._connected = false;
    this._stopHeartbeat();
    // Reject all in-flight service calls so callers don't hang.
    this._pendingCalls.forEach((pending) => pending.reject(new DisconnectedError()));
    this._pendingCalls.clear();

    this.emitter.emit('close', event);
    if (this._closed) return;

    // New waiters block until the next successful connection.
    this._resetConnecting();
    this._scheduleReconnect();
  }

  /** Schedule a reconnect with exponential backoff; idempotent (no double timers). */
  _scheduleReconnect() {
    if (this._closed || this._reconnectTimer) return;
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** this._reconnectAttempts, RECONNECT_MAX_MS);
    this._reconnectAttempts++;
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = undefined;
      this._connect();
    }, delay);
  }

  /**
   * Liveness watchdog: drips (and any other inbound traffic) reset _lastActivityAt via
   * the 'message' handler. If they stop for LIVENESS_TIMEOUT_MS the link is dead —
   * force a reconnect rather than wait for the browser's delayed 'close'.
   */
  _startHeartbeat() {
    this._stopHeartbeat();
    this._lastActivityAt = Date.now();
    this._heartbeatTimer = setInterval(() => {
      if (!this._connected) return;
      if (Date.now() - this._lastActivityAt > LIVENESS_TIMEOUT_MS) {
        this.emitter.emit(
          'error',
          new Error(`Foxglove connection timed out (no drips for ${LIVENESS_TIMEOUT_MS}ms)`),
        );
        this._dropDeadConnection();
      }
    }, LIVENESS_CHECK_MS);
  }

  _stopHeartbeat() {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = undefined;
    }
  }

  /**
   * Force a hung/dead socket closed and reconnect immediately. Used by the liveness
   * watchdog: we can't wait for the browser's 'close' (which may be minutes away on a
   * silent TCP drop), so we tear down and reschedule ourselves. The old socket's
   * eventual 'close' is ignored via the stale-client guard in _connect.
   */
  _dropDeadConnection() {
    this._stopHeartbeat();
    this._connected = false;
    try {
      this._ws?.close();
    } catch {
      /* ignore */
    }
    this._onClose();
  }

  /** Re-establish everything already advertised after a (re)connect. */
  _reestablish() {
    this._subscriptions.forEach((_entry, topic) => this._establishSubscription(topic));
    this._publishers.forEach((_entry, topic) => this._establishPublisher(topic));
  }

  close() {
    this._closed = true;
    this._stopHeartbeat();
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = undefined;
    }
    this._client.close();
  }

  isConnected() {
    return this._connected;
  }

  getTopics() {
    const topics: string[] = [];
    const types: string[] = [];
    this._channelsByName.forEach((channel, topic) => {
      topics.push(topic);
      types.push(channel.schemaName);
    });
    return {
      topics,
      types,
    };
  }

  async getServices() {
    await this._connecting;
    return new Promise<string[]>((resolve) => {
      const listener = (event: ConnectionGraphUpdate) => {
        this._client.off('connectionGraphUpdate', listener);
        this._client.unsubscribeConnectionGraph();
        resolve(event.advertisedServices.map((service) => service.name));
      };
      this._client.on('connectionGraphUpdate', listener);
      this._client.subscribeConnectionGraph();
    });
  }

  getTopicType(topic: string) {
    return this._channelsByName.get(topic)?.schemaName;
  }

  getServiceType(service: string) {
    return this._servicesByName.get(service)?.type;
  }

  async createPublisher<T>(name: string, messageType?: string): Promise<Publisher<T>> {
    await this._connecting;
    let entry = this._publishers.get(name);
    if (!entry) {
      entry = { messageType: messageType || undefined, refCount: 0 };
      this._publishers.set(name, entry);
    }
    entry.refCount++;
    this._establishPublisher(name); // establishes now if advertised, else on 'advertise'

    return {
      publish: (message: T) => {
        const e = this._publishers.get(name);
        if (!this._connected || !e?.writer || e.pubId === undefined) return; // drop while reconnecting
        this._client.sendMessage(e.pubId, e.writer.writeMessage(message));
      },
      unadvertise: () => {
        const e = this._publishers.get(name);
        if (!e) return;
        e.refCount--;
        if (e.refCount <= 0) {
          if (e.pubId !== undefined && this._connected) this._client.unadvertise(e.pubId);
          this._publishers.delete(name);
        }
      },
    };
  }

  /**
   * Advertise the client publisher if its topic is already in the graph; otherwise
   * defer (the 'advertise' handler re-invokes this when the topic appears). Never
   * throws for a missing topic — a topic that isn't there yet is normal.
   */
  _establishPublisher(name: string): void {
    const entry = this._publishers.get(name);
    if (!entry || entry.pubId !== undefined) return;
    const channel = this._channelsByName.get(name);
    if (!channel) return; // not advertised yet; retried on 'advertise'
    try {
      const schemaName = entry.messageType ?? channel.schemaName;
      entry.messageType = schemaName;
      entry.writer = this._getMessageWriter(channel);
      entry.pubId = this._client.advertise({ topic: name, encoding: 'cdr', schemaName });
    } catch (err) {
      this.emitter.emit('error', err as Error);
    }
  }

  async createSubscription<T>(
    name: string,
    callback: (message: T) => void,
  ): Promise<Subscription> {
    await this._connecting;
    let entry = this._subscriptions.get(name);
    if (!entry) {
      entry = { callbacks: new Set() };
      this._subscriptions.set(name, entry);
    }
    entry.callbacks.add(callback as Subscriber);
    this._establishSubscription(name); // establishes now if advertised, else on 'advertise'

    return {
      unsubscribe: () => {
        const e = this._subscriptions.get(name);
        if (!e) return;
        e.callbacks.delete(callback as Subscriber);
        if (e.callbacks.size === 0) {
          if (e.subId !== undefined) {
            if (this._connected) this._client.unsubscribe(e.subId);
            this._subTopicById.delete(e.subId);
          }
          this._subscriptions.delete(name);
        }
      },
    };
  }

  /**
   * Subscribe if the topic is already in the graph; otherwise defer (the 'advertise'
   * handler re-invokes this when the topic appears). Never throws for a missing topic:
   * a topic that isn't advertised yet is normal — the subscription stays pending and
   * activates whenever the topic shows up.
   */
  _establishSubscription(name: string): void {
    const entry = this._subscriptions.get(name);
    if (!entry || entry.subId !== undefined) return;
    const channel = this._channelsByName.get(name);
    if (!channel) return; // not advertised yet; retried on 'advertise'
    try {
      entry.reader = this._getMessageReader(channel);
      const subId = this._client.subscribe(channel.id);
      entry.subId = subId;
      this._subTopicById.set(subId, name);
    } catch (err) {
      this.emitter.emit('error', err as Error);
    }
  }

  async sendServiceRequest<Request, Response>(
    name: string,
    request: Request,
    options?: ServiceCallOptions,
  ): Promise<Response> {
    await this._connecting;
    const service = await this._getService(name);
    const writer = this._getMessageWriter(service);
    const reader = this._getMessageReader(service);

    const callId = this._callId++;
    const timeoutMs = options?.timeoutMs ?? SERVICE_TIMEOUT_MS;

    return new Promise<Response>((resolve, reject) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      let onResponse: (event: ServiceCallResponse) => void;
      let onFailure: (event: ServiceCallFailure) => void;

      const cleanup = () => {
        this._client.off('serviceCallResponse', onResponse);
        this._client.off('serviceCallFailure', onFailure);
        if (timer) clearTimeout(timer);
        this._pendingCalls.delete(callId);
      };

      onResponse = (event: ServiceCallResponse) => {
        if (event.serviceId !== service.id || event.callId !== callId) return;
        cleanup();
        try {
          resolve(reader.readMessage(event.data) as Response);
        } catch (err) {
          reject(err as Error);
        }
      };
      onFailure = (event: ServiceCallFailure) => {
        if (event.serviceId !== service.id || event.callId !== callId) return;
        cleanup();
        reject(new Error(event.message || `Service call to "${name}" failed`));
      };

      this._client.on('serviceCallResponse', onResponse);
      this._client.on('serviceCallFailure', onFailure);
      // Registered so _onClose can reject it if the socket drops mid-call.
      this._pendingCalls.set(callId, {
        reject: (err: Error) => {
          cleanup();
          reject(err);
        },
      });

      if (timeoutMs > 0 && Number.isFinite(timeoutMs)) {
        timer = setTimeout(() => {
          cleanup();
          reject(new Error(`Service call to "${name}" timed out after ${timeoutMs}ms`));
        }, timeoutMs);
      }

      this._client.sendServiceCallRequest({
        serviceId: service.id,
        callId,
        encoding: 'cdr',
        data: new DataView(writer.writeMessage(request).buffer),
      });
    });
  }

  async getParameter(name: string) {
    await this._connecting;
    const paramId = (this._paramId++).toString();
    return new Promise<ParameterValue>((resolve) => {
      const listener = (event: ParameterValues) => {
        if (event.parameters[0]?.name === name && event.id === paramId) {
          this._client.off('parameterValues', listener);
          resolve(event.parameters[0].value);
        }
      };
      this._client.on('parameterValues', listener);
      this._client.getParameters([name], paramId);
    });
  }

  async setParameter(
    name: string,
    value: ParameterValue,
    type?: 'byte_array' | 'float64' | 'float64_array',
  ) {
    await this._connecting;
    const paramId = (this._paramId++).toString();
    return new Promise<Parameter>((resolve) => {
      const listener = (event: ParameterValues) => {
        if (event.parameters[0]?.name === name && event.id === paramId) {
          this._client.off('parameterValues', listener);
          resolve(event.parameters[0]);
        }
      };
      this._client.on('parameterValues', listener);
      this._client.setParameters([{ name: name, value, type }], paramId);
    });
  }

  /**
   * Resolve a service, waiting (bounded) for it to be advertised if not yet seen.
   * Services keep the discovery timeout (a call must eventually resolve or reject);
   * topic subscribe/publish do NOT — see _establishSubscription/_establishPublisher.
   */
  async _getService(name: string): Promise<Service> {
    const existing = this._servicesByName.get(name);
    if (existing) return existing;
    return new Promise<Service>((resolve, reject) => {
      const timer = setTimeout(() => {
        this._client.off('advertiseServices', listener);
        reject(
          new Error(
            `Timed out after ${DISCOVERY_TIMEOUT_MS}ms waiting for service "${name}" to be advertised`,
          ),
        );
      }, DISCOVERY_TIMEOUT_MS);
      const listener = (services: Service[]) => {
        const service = services.find((s) => s.name === name);
        if (service) {
          clearTimeout(timer);
          this._client.off('advertiseServices', listener);
          resolve(service);
        }
      };
      this._client.on('advertiseServices', listener);
    });
  }

  // --- schema resolution (nested-first; carries the service-fix fork's read) ---

  _getMessageReader(channelOrService: Channel | Service): Ros2MessageReader {
    const name =
      'schemaName' in channelOrService
        ? channelOrService.schemaName
        : 'response' in channelOrService
          ? channelOrService.response?.schemaName
          : undefined;
    const schemaEncoding =
      'schemaEncoding' in channelOrService
        ? channelOrService.schemaEncoding
        : 'response' in channelOrService
          ? channelOrService.response?.schemaEncoding
          : undefined;
    const schema =
      'schema' in channelOrService
        ? channelOrService.schema
        : 'response' in channelOrService
          ? channelOrService.response?.schema
          : 'responseSchema' in channelOrService
            ? channelOrService.responseSchema
            : undefined;
    if (name === undefined) throw new Error('Cannot build reader: schema name is undefined');
    if (schema === undefined) throw new Error(`Cannot build reader: schema for "${name}" is undefined`);
    const cached = this._messageReaders.get(name);
    if (cached) return cached;
    const reader = new Ros2MessageReader(
      schemaEncoding === 'ros2idl' ? parseRos2idl(schema) : parse(schema, { ros2: true }),
    );
    this._messageReaders.set(name, reader);
    return reader;
  }

  _getMessageWriter(channelOrService: Channel | Service): Ros2MessageWriter {
    const name =
      'schemaName' in channelOrService
        ? channelOrService.schemaName
        : channelOrService.type;
    const schemaEncoding =
      'schemaEncoding' in channelOrService
        ? channelOrService.schemaEncoding
        : 'request' in channelOrService
          ? channelOrService.request?.schemaEncoding
          : undefined;
    const schema =
      'schema' in channelOrService
        ? channelOrService.schema
        : 'request' in channelOrService
          ? channelOrService.request?.schema
          : 'requestSchema' in channelOrService
            ? channelOrService.requestSchema
            : undefined;
    if (name === undefined) throw new Error('Cannot build writer: schema name is undefined');
    if (schema === undefined) throw new Error(`Cannot build writer: schema for "${name}" is undefined`);
    const cached = this._messageWriters.get(name);
    if (cached) return cached;
    const writer = new Ros2MessageWriter(
      schemaEncoding === 'ros2idl' ? parseRos2idl(schema) : parse(schema, { ros2: true }),
    );
    this._messageWriters.set(name, writer);
    return writer;
  }
}
